import { enableProdMode, importProvidersFrom, ErrorHandler } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from '@sentry/angular';


import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { entityConfig } from './app/data/entity-metadata';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { EffectsModule } from '@ngrx/effects';
import { reducers, effects } from './app/store';
import { StoreModule } from '@ngrx/store';
import { PaymentModule } from './app/modules/payment/payment.module';
import { SettingsModule } from './app/modules/settings/settings.module';
import { RoutingModule } from './app/modules/routing/routing.module';
import { ShipmentModule } from './app/modules/shipment/shipment.module';
import { SharedModule } from './app/modules/shared/shared.module';
import { PickupModule } from './app/modules/pickup/pickup.module';
import { InvoicesModule } from './app/modules/invoices/invoices.module';
import { AddressBookModule } from './app/modules/address-books/address-book.module';
import { DashboardModule } from './app/modules/dashboard/dashboard.module';
import { NavigationModule } from './app/modules/navigation/navigation.module';
import { RouterModule } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { MaterialModule } from './app/modules/material/material.module';
import { enCA } from 'date-fns/locale';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { dataServiceConfig } from './app/data/data-service-config';
import { DefaultDataServiceConfig, EntityDataModule } from '@ngrx/data';
import { DebugInterceptor } from './app/interceptors/debug.interceptor';
import { AuthenticationInterceptor } from './app/interceptors/authentication.interceptor';
import { HTTP_INTERCEPTORS, provideHttpClient } from '@angular/common/http';
import { AppMatPaginatorIntl } from './app/material/app-mat-paginator-intl';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { LanguageInterceptor } from './app/interceptors/language.interceptor';
import { ShipmentInsuranceValidator } from './app/validators/shipment-insurance.validator';
import { STATE_VALIDATORS } from './app/constants/state-validators';
import { GeoValidator } from './app/validators/geo.validator';
import { ShipmentAddressesValidator } from './app/validators/shipment-addresses.validator';

const CUSTOM_DATE_FORMATS = {
    parse: {
        dateInput: 'dd-MM-yyyy',
    },
    display: {
        dateInput: 'dd-MM-yyyy',
        monthYearLabel: 'MMMM yyyy',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM yyyy',
    },
};

if (environment.production) {
    enableProdMode();
}

Sentry.init({
    dsn: 'https://6614d18fb6f2408fbb918726d1c54a89@o158903.ingest.sentry.io/6445990',
    // integrations: [Sentry.browserTracingIntegration()],
    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    release: environment.release,
    environment: environment.production ? 'production' : 'development'
});

bootstrapApplication(AppComponent, {
    providers: [
        importProvidersFrom(
            MaterialModule,
            BrowserModule,
            RouterModule,
            RoutingModule,
            SharedModule,
            NavigationModule,
            DashboardModule,
            AddressBookModule,
            InvoicesModule,
            PickupModule,
            SharedModule,
            ShipmentModule,
            SettingsModule,
            PaymentModule,
            StoreModule.forRoot(reducers, {}),
            EffectsModule.forRoot(effects),
            StoreDevtoolsModule.instrument({ maxAge: 20, logOnly: environment.production, connectInZone: true, trace: false }),
            EntityDataModule.forRoot(entityConfig),
            StoreRouterConnectingModule.forRoot()
        ),
        { provide: HTTP_INTERCEPTORS, useClass: AuthenticationInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: DebugInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
        { provide: STATE_VALIDATORS, useClass: ShipmentAddressesValidator, multi: true },
        { provide: STATE_VALIDATORS, useClass: ShipmentInsuranceValidator, multi: true },
        { provide: STATE_VALIDATORS, useClass: GeoValidator, multi: true },
        { provide: DefaultDataServiceConfig, useValue: dataServiceConfig },
        { provide: DateAdapter, useClass: DateFnsAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: enCA },
        { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) },
        { provide: MatPaginatorIntl, useClass: AppMatPaginatorIntl },
        provideAnimations(),
        provideHttpClient()
    ]
})
    .catch(err => console.error(err));
